import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { convertFromGobodoRecord } from './gobodoRecordService';

// Static SF user ID / email → display name mapping (generated from RDS users table)
let staticUserNames: Record<string, string> = {};
try {
  const mapping = require('../../config/sfUserMapping.generated.json');
  staticUserNames = mapping?.users ?? {};
} catch { /* file may not exist */ }

/** Resolve a SF user ID or email to a display name via static mapping */
function resolveFromStaticMapping(value: string): string {
  if (!value) return '';
  return staticUserNames[value] || staticUserNames[value.replace('.invalid', '')] || '';
}

const USER_LOOKUP_CACHE_DURATION_SECONDS = 120;
const USER_SF_ID_FIELD = `sf_id${cleanString(GobodoConfig.customSuffix()) || '__c'}`;

export interface GraphUserLookupResult {
  id: string;
  key: string;
  sfId: string;
  displayName: string;
  email?: string;
  username?: string;
}

const userLookupPromiseCache = new Map<string, Promise<GraphUserLookupResult | null>>();
const userLookupByObjectIdPromiseCache = new Map<string, Promise<GraphUserLookupResult | null>>();
const ownerRelationLookupPromiseCache = new Map<string, Promise<GraphUserLookupResult | null>>();

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function knownSuffixes(): string[] {
  const configured = cleanString(GobodoConfig.customSuffix());
  return Array.from(
    new Set([configured, '__stg3_c', '__stg_c', '__dev_c', '__stg', '__c'].filter(Boolean))
  );
}

function userTypeKeys(): string[] {
  return ['USER'];
}

/** Graph object types for RDS-synced Salesforce users (not portal USER). */
function syncedSalesforceUserGraphTypeKeys(): string[] {
  const suffix = cleanString(GobodoConfig.customSuffix()) || '__c';
  return Array.from(new Set([
    `User${suffix}`,
  ]));
}

function stripKnownSuffix(value: string): string {
  const raw = cleanString(value);
  if (!raw) return '';
  for (const suffix of knownSuffixes()) {
    if (raw.toLowerCase().endsWith(suffix.toLowerCase())) {
      return raw.slice(0, raw.length - suffix.length);
    }
  }
  return raw;
}

function sfIdCandidates(raw: string): string[] {
  const trimmed = cleanString(raw);
  if (!trimmed) return [];
  const stripped = stripKnownSuffix(trimmed);
  const candidates = new Set([trimmed, stripped].filter(Boolean));
  // For Keycloak UUIDs, also include lowercased form — UUID map keys are stored lowercased.
  if (KEYCLOAK_UUID_RE.test(trimmed)) {
    candidates.add(trimmed.toLowerCase());
    if (stripped) candidates.add(stripped.toLowerCase());
  }
  return Array.from(candidates);
}

function userLookupCacheKey(sfId: string): string {
  return stripKnownSuffix(sfId).toLowerCase() || cleanString(sfId).toLowerCase();
}

function pickUserDisplayName(raw: Record<string, unknown>, fallback: string): string {
  const data = raw.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : {};
  const flat = { ...raw, ...data };
  // Check Gobodo portal user fields (multiple naming conventions)
  const first = cleanString(
    flat['zeusportal.member.firstname'] ??
    raw.firstname ?? flat.FirstName__c ?? flat.firstName ?? flat.firstname ?? flat.first_name
  );
  const last = cleanString(
    flat['zeusportal.member.lastname'] ??
    raw.lastname ?? flat.LastName__c ?? flat.lastName ?? flat.lastname ?? flat.last_name
  );
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  // Check name fields
  const name = cleanString(
    flat.Name__c ?? flat.name ?? raw.name ?? flat.FullName ?? flat.fullName
  );
  if (name) return name;
  // Check static mapping by SF ID, email, or label
  const sfId = cleanString(flat[USER_SF_ID_FIELD] ?? flat.sf_id ?? flat.sfId ?? flat.rdsSfId);
  const email = cleanString(flat.Email__c ?? flat.email);
  const label = cleanString(raw.label ?? flat.label);
  const staticName = resolveFromStaticMapping(sfId) || resolveFromStaticMapping(email) || resolveFromStaticMapping(label) || resolveFromStaticMapping(fallback);
  if (staticName) return staticName;
  // Check label (but skip if it looks like an email or ID)
  if (label && !label.includes('@') && !/^[0-9a-zA-Z]{15,18}$/.test(label)) return label;
  if (fallback && !fallback.includes('@') && !/^[0-9a-zA-Z]{15,18}$/.test(fallback)) return fallback;
  return email || cleanString(flat.Username__c ?? flat.username) || fallback;
}

function userResultFromTraversalRecord(
  raw: Record<string, unknown>,
  fallbackSfId: string,
  fallbackDisplay: string
): GraphUserLookupResult {
  const objectTypeKey = cleanString(
    raw.objecttypekey ??
      raw.objectTypeKey ??
      ((raw.objecttype as { key?: unknown } | undefined)?.key ?? '')
  ) || userTypeKeys()[0];
  const converted = convertFromGobodoRecord(raw, objectTypeKey) as Record<string, unknown>;
  const data = raw.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : {};
  const key = cleanString(converted.key ?? raw.key);
  const id = cleanString(converted.id ?? raw.id);
  const sfId = cleanString(
    converted[USER_SF_ID_FIELD] ??
      converted.sf_id ??
      converted.sfId ??
      converted.rdsSfId ??
      data[USER_SF_ID_FIELD] ??
      data.sf_id ??
      data.rdsSfId
  ) || fallbackSfId;
  const displayName = pickUserDisplayName(raw, fallbackDisplay || cleanString(raw.label ?? key ?? id ?? sfId));
  const email = cleanString(
    converted.Email__c ??
      converted.email ??
      data.Email__c ??
      data.email
  );
  const username = cleanString(
    converted.Username__c ??
      converted.username ??
      data.Username__c ??
      data.username
  );
  return {
    id,
    key,
    sfId,
    displayName,
    ...(email ? { email } : {}),
    ...(username ? { username } : {}),
  };
}

async function traverseUserBySfId(typeKey: string, sfId: string): Promise<GraphUserLookupResult | null> {
  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);
  const input = new GobodoApi.GraphTraversalInput();
  input.setStartObjectTypes([typeKey]);
  input.setEndObjectTypes([typeKey]);
  input.setMaxPathLength(0);
  try {
    const filter = new GobodoApi.IndexFilter();
    filter.setIndexName(USER_SF_ID_FIELD);
    filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
    filter.setValues([sfId]);
    input.setStartObjectFieldFilters([filter]);
  } catch {
    return null;
  }
  const cachekey = ['user-by-sf-id', typeKey, sfId];
  const result = await retryWithBackoff(
    () =>
      traversalApi.traverseObjects(input, {
        cachekey,
        cachegroup: 'user-by-sf-id',
        cacheduration: String(USER_LOOKUP_CACHE_DURATION_SECONDS),
      }),
    { maxRetries: 2, initialDelayMs: 750 }
  ).catch(() => null);
  if (!Array.isArray(result) || result.length === 0) return null;
  const raw = result[0] as Record<string, unknown>;
  return userResultFromTraversalRecord(raw, sfId, cleanString(raw.label ?? sfId));
}

async function traverseUserByOwnerRelation(
  startObjectKey: string,
  relationshipType: string,
  endObjectType: string
): Promise<GraphUserLookupResult | null> {
  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectKey(startObjectKey);
  input.setEndObjectTypes([endObjectType]);
  input.setRelationshipTypes([relationshipType]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
  input.setMaxPathLength(1);
  const cachekey = ['user-by-owner-rel', relationshipType, endObjectType, startObjectKey];
  const result = await retryWithBackoff(
    () =>
      traversalApi.traverseObjects(input, {
        cachekey,
        cachegroup: 'user-by-owner-rel',
        cacheduration: String(USER_LOOKUP_CACHE_DURATION_SECONDS),
      }),
    { maxRetries: 2, initialDelayMs: 750 }
  ).catch(() => null);
  if (!Array.isArray(result) || result.length === 0) return null;
  const raw = result[0] as Record<string, unknown>;
  return userResultFromTraversalRecord(raw, '', cleanString(raw.label ?? startObjectKey));
}

/** Cache of all user graph objects, keyed by sf_id (fetched once). */
let allUsersCache: Map<string, Record<string, unknown>> | null = null;
let allUsersFetchPromise: Promise<Map<string, Record<string, unknown>>> | null = null;

async function getAllUserObjects(): Promise<Map<string, Record<string, unknown>>> {
  if (allUsersCache) return allUsersCache;
  if (allUsersFetchPromise) return allUsersFetchPromise;

  allUsersFetchPromise = (async () => {
    const map = new Map<string, Record<string, unknown>>();
    try {
      const apiClient = getGobodoClient();
      const objApi = new GobodoApi.GraphObjectsApi(apiClient);
      const graphId = GobodoConfig.graph();
      const typeKeys = Array.from(new Set([
        ...userTypeKeys(),
        ...syncedSalesforceUserGraphTypeKeys(),
      ]));
      for (const typeKey of typeKeys) {
        const users = await retryWithBackoff(
          () => objApi.getObjectsInGraphByType(graphId, typeKey),
          { maxRetries: 2, initialDelayMs: 1000 }
        ).catch(() => []);
        if (Array.isArray(users)) {
          for (const u of users) {
            const d = (u as Record<string, unknown>).data as Record<string, unknown> || {};
            const sfId = cleanString(d.sf_id ?? d[USER_SF_ID_FIELD] ?? d['sf_id__stg3_c'] ?? d.rdsSfId);
            const stripped = stripKnownSuffix(sfId);
            if (stripped) {
              map.set(stripped, u as Record<string, unknown>);
              map.set(sfId, u as Record<string, unknown>);
            }
            // Also key by any Keycloak-UUID-shaped value found anywhere on the user object,
            // so audit fields like LastModifiedById__c that hold Keycloak `sub` claims can resolve.
            const flat = { ...(u as Record<string, unknown>), ...d };
            for (const value of Object.values(flat)) {
              const sv = cleanString(value);
              if (sv && looksLikeKeycloakUuid(sv)) {
                map.set(sv.toLowerCase(), u as Record<string, unknown>);
              }
            }
          }
        }
      }
    } catch { /* ignore — traversal fallback will still try */ }
    allUsersCache = map;
    return map;
  })();

  return allUsersFetchPromise;
}

async function lookupGraphUserBySfId(rawSfId: string): Promise<GraphUserLookupResult | null> {
  if (!USE_GOBODO_API) return null;

  // Try the cached all-users map first (fast, no API call per user)
  const usersMap = await getAllUserObjects();
  for (const candidate of sfIdCandidates(rawSfId)) {
    const raw = usersMap.get(candidate) || usersMap.get(stripKnownSuffix(candidate));
    if (raw) {
      return userResultFromTraversalRecord(raw, candidate, cleanString((raw as any).label ?? candidate));
    }
  }

  // Fallback to traversal (may work for some configurations)
  for (const candidate of sfIdCandidates(rawSfId)) {
    for (const typeKey of userTypeKeys()) {
      const match = await traverseUserBySfId(typeKey, candidate);
      if (match) return match;
    }
  }

  const graphUserTypes = syncedSalesforceUserGraphTypeKeys();
  if (graphUserTypes.length > 0) {
    for (const candidate of sfIdCandidates(rawSfId)) {
      for (const graphUserType of graphUserTypes) {
        const match = await traverseUserBySfId(graphUserType, candidate);
        if (match) return match;
      }
    }
  }
  return null;
}

async function lookupGraphUserByObjectId(rawObjectId: string): Promise<GraphUserLookupResult | null> {
  if (!USE_GOBODO_API) return null;
  const objectId = cleanString(rawObjectId);
  if (!/^\d+$/.test(objectId)) return null;
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);
  const raw = await retryWithBackoff(
    () => objectsApi.getObjectById(Number(objectId)),
    { maxRetries: 2, initialDelayMs: 750 }
  ).catch(() => null);
  if (!raw || typeof raw !== 'object') return null;
  return userResultFromTraversalRecord(raw as Record<string, unknown>, '', objectId);
}

const KEYCLOAK_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function looksLikeKeycloakUuid(value: string | undefined | null): boolean {
  return KEYCLOAK_UUID_RE.test(cleanString(value));
}

export function looksLikeGraphUserSfId(value: string | undefined | null): boolean {
  const raw = cleanString(value);
  if (!raw) return false;
  if (/^\d+$/.test(raw)) return true;
  if (raw.length >= 15 && /^[A-Za-z0-9_]+(?:__[A-Za-z0-9]+)?$/.test(raw)) return true;
  if (looksLikeKeycloakUuid(raw)) return true;
  return false;
}

export async function findGraphUserBySfId(sfId: string): Promise<GraphUserLookupResult | null> {
  const key = userLookupCacheKey(sfId);
  if (!key) return null;
  let pending = userLookupPromiseCache.get(key);
  if (!pending) {
    pending = lookupGraphUserBySfId(sfId);
    userLookupPromiseCache.set(key, pending);
  }
  try {
    return await pending;
  } catch {
    userLookupPromiseCache.delete(key);
    return null;
  }
}

export async function findGraphUserByObjectId(objectId: string): Promise<GraphUserLookupResult | null> {
  const key = cleanString(objectId);
  if (!key) return null;
  let pending = userLookupByObjectIdPromiseCache.get(key);
  if (!pending) {
    pending = lookupGraphUserByObjectId(key);
    userLookupByObjectIdPromiseCache.set(key, pending);
  }
  try {
    return await pending;
  } catch {
    userLookupByObjectIdPromiseCache.delete(key);
    return null;
  }
}

export async function findGraphUserByOwnerRelation(input: {
  startObjectKey: string;
  relationshipType: string;
}): Promise<GraphUserLookupResult | null> {
  const startObjectKey = cleanString(input.startObjectKey);
  const relationshipType = cleanString(input.relationshipType);
  if (!USE_GOBODO_API || !startObjectKey || !relationshipType) return null;
  const cacheKey = [relationshipType.toLowerCase(), startObjectKey.toLowerCase()].join('::');
  let pending = ownerRelationLookupPromiseCache.get(cacheKey);
  if (!pending) {
    pending = (async () => {
      for (const userType of userTypeKeys()) {
        const match = await traverseUserByOwnerRelation(startObjectKey, relationshipType, userType);
        if (match) return match;
      }
      for (const graphUserType of syncedSalesforceUserGraphTypeKeys()) {
        const match = await traverseUserByOwnerRelation(startObjectKey, relationshipType, graphUserType);
        if (match) return match;
      }
      return null;
    })();
    ownerRelationLookupPromiseCache.set(cacheKey, pending);
  }
  try {
    return await pending;
  } catch {
    ownerRelationLookupPromiseCache.delete(cacheKey);
    return null;
  }
}

export async function resolveGraphUserNamesBySfIds(sfIds: readonly string[]): Promise<Map<string, string>> {
  const uniqueIds = Array.from(new Set(sfIds.map((value) => cleanString(value)).filter(Boolean)));
  if (uniqueIds.length === 0) return new Map();
  const out = new Map<string, string>();

  // First pass: resolve from static mapping (instant, no API calls)
  const unresolved: string[] = [];
  for (const sfId of uniqueIds) {
    const staticName = resolveFromStaticMapping(sfId);
    if (staticName) {
      for (const candidate of sfIdCandidates(sfId)) {
        out.set(candidate, staticName);
        out.set(stripKnownSuffix(candidate), staticName);
      }
    } else {
      unresolved.push(sfId);
    }
  }

  // Second pass: resolve remaining via Gobodo API
  if (unresolved.length > 0 && USE_GOBODO_API) {
    const results = await Promise.all(unresolved.map(async (id) => {
      // Check if this is a numeric Gobodo object ID vs a SF ID
      if (/^\d+$/.test(id)) {
        // Numeric ID - try to resolve via Gobodo object lookup
        const match = await findGraphUserByObjectId(id);
        return [id, match] as const;
      } else {
        // SF-style ID - use SF ID lookup
        const match = await findGraphUserBySfId(id);
        return [id, match] as const;
      }
    }));
    for (const [rawId, match] of results) {
      if (!match?.displayName) continue;
      // For numeric IDs, just set the exact ID
      if (/^\d+$/.test(rawId)) {
        out.set(rawId, match.displayName);
      } else {
        // For SF IDs, also set stripped versions
        for (const candidate of sfIdCandidates(rawId)) {
          out.set(candidate, match.displayName);
          out.set(stripKnownSuffix(candidate), match.displayName);
        }
      }
    }
  }

  return out;
}
